import { createInstance } from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import ChainedBackend from 'i18next-chained-backend'
import HttpBackend from 'i18next-http-backend'
import resourcesToBackend from 'i18next-resources-to-backend'
import { initReactI18next } from 'react-i18next'
import { getLanguageDetectorOptions, setLocale } from 'src/stores/locale'
import {
  DEFAULT_LOCALE,
  LOCALE_CODES,
  type SupportedLocaleCodes,
} from './constants'
import { I18N_DEBUG_POSTPROCESSOR_NAME, installI18nDebug } from './i18nDebug'
import { loadBundledResource } from './resources'
import { simpleLocalizeLoadPath } from './utils'

const i18n = createInstance()
installI18nDebug(i18n)

/**
 * Dev escape hatch: prefer bundled local JSON over SimpleLocalize CDN. When
 * the CDN is missing a recently-added key, i18next renders the raw `ns:key`
 * (visible as gray text in the UI). Flipping this swaps backend order so
 * local resources resolve first and missing keys render their bundled text.
 *
 * Gated by `import.meta.env.DEV` so a stray `VITE_I18N_LOCAL_FALLBACK=true`
 * in a production build can't ship stale bundled strings to real users.
 */
const localI18nFirst =
  import.meta.env.DEV && import.meta.env.VITE_I18N_LOCAL_FALLBACK === 'true'
const httpBackendOptions = { loadPath: simpleLocalizeLoadPath }
const localBackend = resourcesToBackend(loadBundledResource)

if (localI18nFirst) {
  // eslint-disable-next-line no-console
  console.info(
    '[i18n] VITE_I18N_LOCAL_FALLBACK=true — bundled local JSON resolves before SimpleLocalize CDN.',
  )
}

const initPromise = i18n
  .use(ChainedBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    load: 'languageOnly',
    backend: {
      backends: localI18nFirst
        ? [localBackend, HttpBackend]
        : [HttpBackend, localBackend],
      backendOptions: localI18nFirst
        ? [{}, httpBackendOptions]
        : [httpBackendOptions, {}],
    },
    detection: getLanguageDetectorOptions(),
    postProcess: [I18N_DEBUG_POSTPROCESSOR_NAME],
    ns: [
      'action',
      'artwork',
      'auth',
      'comment',
      'common',
      'error',
      'generation',
      'growth',
      'manga',
      'market',
      'membership',
      'trainlora',
      'user',
      'workflow',
      'agent',
      'model',
      'blackboard',
    ],
    defaultNS: 'common',
    supportedLngs: LOCALE_CODES,
    fallbackLng: {
      default: [DEFAULT_LOCALE],
    },
    returnEmptyString: false,
    nonExplicitSupportedLngs: true,
    debug: false,
    showSupportNotice: false,
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
    react: {
      bindI18n: 'languageChanged',
      // Disable react-i18next's Suspense integration. With it on
      // (`useSuspense: true` is the default), every `useTranslation`
      // call throws a promise until the namespace is loaded — which
      // during streaming SSR (`renderToPipeableStream({ onShellReady
      // })`) makes the nearest `<Suspense>` boundary defer its
      // reveal. The body chunk emits the real content into a
      // `<div hidden id="S:N">…</div>` and queues a `<script>$RC(B:N,
      // S:N)</script>`, so the boundary stays as
      // `<!--$?--><template id="B:N"></template><!--/$-->` until
      // hydration runs the reveal scripts. Users browsing with
      // JavaScript disabled (or before hydration completes) then see
      // a permanently empty slot wherever a Suspense boundary wraps
      // a translated subtree — most visibly the `<MediaPreview>`
      // grid under `<ArtworkDetailAuthorArtworks>` ("更多精彩作品" /
      // recommended-author thumbnails).
      // Switching to `useSuspense: false` makes `useTranslation`
      // resolve synchronously: when a namespace isn't loaded yet, t()
      // returns the key (or fallback) instead of throwing. We pair
      // this with `entry.server.tsx`'s pre-render
      // `i18n.loadNamespaces([...])` so SSR still ships translated
      // copy for the common namespaces; only late/lazy-loaded
      // namespaces will fall back to the key for the first paint.
      useSuspense: false,
    },
  })

i18n.on('initialized', () => {
  const resolvedLang = (i18n.resolvedLanguage ??
    DEFAULT_LOCALE) as SupportedLocaleCodes
  setLocale(resolvedLang)
})

// i18n.on('languageChanged', lng => {
//   // update dayjs locale here
//   // FIXME: doesn't seem to work
//   dayjs.locale(lng)
// })

/**
 * Get the resolved language with fallback to DEFAULT_LOCALE
 * Helper function to avoid code duplication and ensure type safety
 */
export const getResolvedLanguage = (): SupportedLocaleCodes =>
  (i18n.resolvedLanguage ?? DEFAULT_LOCALE) as SupportedLocaleCodes

if (typeof document !== 'undefined') {
  // set initial lang attribute on load (once)
  document.documentElement.setAttribute('lang', getResolvedLanguage())

  // update lang attribute on everytime language changes
  i18n.on('languageChanged', lng => {
    document.documentElement.setAttribute('lang', getResolvedLanguage())
  })
}

/**
 * Namespaces that the SSR pipeline pre-loads in `entry.server.tsx` before
 * rendering. The CSR entry point (`entry.client.tsx`) awaits the same set
 * before calling `hydrateRoot`, so the server-rendered translated text
 * (e.g. the `<MembershipHeaderBanner>` "PixAI 會員" label or the
 * `<ArtworkDetailAuthorArtworks>` "更多精彩作品" heading) matches what the
 * client first commits — without that, the very first hydrate render
 * would call `t('membership:membership.banner.label')` *before* the
 * `membership` resource bundle resolved and (because `useSuspense: false`)
 * fall back to the literal key, triggering a hydration mismatch that
 * forces React 19 to throw away the SSR DOM and re-render the whole tree
 * (which also resets every Apollo `useQuery` subscriber, briefly
 * flashing skeletons through the recommend grid).
 *
 * Keep this in sync with `entry.server.tsx`'s `loadNamespaces([...])`.
 */
export const SSR_PRELOAD_NAMESPACES = [
  'common',
  'user',
  'artwork',
  'error',
  'membership',
  'growth',
  'agent',
  'model',
  'comment',
  'action',
  'generation',
  // `auth` is required by `pages/SignPage.tsx` (used by `/login` and
  // `/sign-up`). The page calls `t('auth:landing.headline', ...)`,
  // `t('auth:landing.faq.items.<key>.q')`, etc. on every section; if
  // we don't preload it, SSR rendering with a non-`en` locale will
  // fall back to the inline default (English), then hydrate would
  // swap to the correct translation and trigger the "throw away SSR
  // DOM" hydration mismatch that the namespace list above is
  // designed to prevent.
  'auth',
  // `manga` is required by `<ArtworkTypeLabel>` (`useTranslation(
  // ['artwork', 'manga'])`) which renders on every artwork card in
  // the home / market / tag listings, and by `<ArtworkListPage>`
  // itself (`useTranslation(['common', 'artwork', 'manga'])`). Without
  // preloading, the first SSR render of these pages throws a Promise
  // from react-i18next's namespace loader (`useSuspense: true`)
  // straight into the nearest `<Suspense>` boundary — the one
  // `<ArtworkListPage>` wraps `<ArtworkListGrid>` in. React 19's
  // streaming SSR then emits `<template id="B:0"></template>` +
  // `<div hidden id="S:N">` + `$RC` swap script for the grid: with
  // JS disabled the swap never runs and the listings render
  // permanently blank (this exactly reproduces the user-reported
  // "/zh — JS 禁用页面没 SSR 渲染" symptom). Preloading manga keeps
  // the boundary transparent so the grid HTML inlines into the
  // first SSR flush.
  'manga',
  // `market` is referenced by `<TagDescription>` / `<ArtworkListPage>`'s
  // filter rendering paths that ship under the same SSR tree as the
  // home grid; same Suspense-trip-then-streaming-swap consequence
  // when missing.
  'market',
] as const

/**
 * Resolves once `i18n.init()` and all the SSR-preloaded namespaces are
 * ready. `entry.client.tsx` awaits this before calling `hydrateRoot`
 * so the first client commit has the same translations the SSR HTML
 * was rendered with.
 */
export const i18nReady: Promise<void> = (async () => {
  await initPromise
  try {
    await i18n.loadNamespaces([...SSR_PRELOAD_NAMESPACES])
  } catch {
    // best-effort — if a namespace fails to load we still want hydrate
    // to proceed; missing keys will gracefully fall back to the key
    // string thanks to `useSuspense: false`.
  }
})()

export { i18n }
export default i18n
